import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ImagePlus, Search, Upload } from 'lucide-react'
import { resizeForUpload } from '../api/imageResize'
import {
  batch,
  displayName,
  fetchMedia,
  fileSize,
  summariseUpload,
  uploadMedia,
  type MediaAsset,
} from '../api/media'
import StudioImage from './StudioImage'

/** Small enough that a failure loses a handful of photos, not an evening. */
const BATCH_SIZE = 5

/**
 * The whole photo library, and the only place to add to it in bulk.
 *
 * **Why it exists.** Photographs could only be added one at a time, through the
 * picker inside the content editor — fine when swapping a hero image, useless
 * for the 183 the studio already owns, and useless again every recital. A
 * folder-ingest endpoint covered the first case in development and does not
 * exist where the studio actually runs (D-140).
 *
 * **Uploading is the job; the grid is context.** So the drop zone is the first
 * thing on the page rather than an action hidden behind a button, and the grid
 * below answers "is it already in here?" without a round trip through search.
 */
export default function MediaLibraryView() {
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)
  const [refused, setRefused] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const { data, isPending } = useQuery({ queryKey: ['media'], queryFn: fetchMedia })

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      setProgress({ done: 0, total: files.length })

      /*
       * Shrunk in the browser before anything is sent (D-126). A camera
       * original is 12 MB against a hero image's 300 KB, so this is both the
       * upload Tonya waits on and the bytes every visitor downloads for years
       * afterwards.
       */
      const prepared = await Promise.all(files.map(resizeForUpload))

      let added = 0
      const rejections: string[] = []

      /*
       * Sequential, not parallel. Six concurrent multipart uploads of studio
       * photography saturate a home connection and make the progress figure
       * meaningless, and B1 is one small instance. Slower and legible beats
       * faster and opaque when somebody is watching it run.
       */
      for (const group of batch(prepared, BATCH_SIZE)) {
        const result = await uploadMedia(group.map((p) => p.file))

        added += result.uploaded.length
        rejections.push(...result.refused)

        setProgress((p) => (p ? { ...p, done: p.done + group.length } : p))

        // Each batch lands in the grid as it completes, so a long upload shows
        // its work rather than sitting still and finishing all at once.
        queryClient.invalidateQueries({ queryKey: ['media'] })
      }

      return { added, rejections }
    },
    onMutate: () => {
      setError(null)
      setOutcome(null)
      setRefused([])
    },
    onSuccess: ({ added, rejections }) => {
      setOutcome(summariseUpload(added, rejections))
      setRefused(rejections)
    },
    onError: (e: Error) => setError(e.message),
    onSettled: () => setProgress(null),
  })

  const send = useCallback(
    (files: FileList | null) => {
      const chosen = Array.from(files ?? []).filter((f) => f.type.startsWith('image/'))
      if (chosen.length > 0) upload.mutate(chosen)
    },
    [upload],
  )

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return data ?? []

    return (data ?? []).filter((a) => a.fileName.toLowerCase().includes(term))
  }, [data, query])

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">Photos</h1>
          <p className="mt-1.5 max-w-[38rem] text-[13.5px] text-ink-3">
            Every picture the site can use. Drop a whole folder in at once &mdash; large
            photographs are shrunk here before they are sent.
          </p>
        </div>

        <Link
          to="/admin/content"
          className="shrink-0 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 no-underline hover:border-ink-3"
        >
          Words on your website
        </Link>
      </header>

      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          send(e.dataTransfer.files)
        }}
        className={`mb-5 rounded-2xl border-2 border-dashed px-5 py-10 text-center ${
          dragging ? 'border-accent bg-accent-soft' : 'border-line-2 bg-surface'
        }`}
      >
        <ImagePlus size={22} strokeWidth={1.75} className="mx-auto mb-2 text-ink-3" />

        <p className="text-[14.5px] font-semibold">Drop photographs here</p>
        <p className="mt-1 text-[13px] text-ink-3">JPEG, PNG, WebP, GIF or AVIF</p>

        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={upload.isPending}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-[13.5px] font-semibold text-on-accent disabled:opacity-60"
        >
          <Upload size={14} strokeWidth={2} />
          {upload.isPending ? 'Uploading...' : 'Choose files'}
        </button>

        <input
          ref={input}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            send(e.target.files)
            // Cleared so choosing the same folder twice still fires a change.
            e.target.value = ''
          }}
        />
      </div>

      {progress && (
        <div className="mb-4 rounded-xl border border-line bg-surface px-4 py-3">
          <p className="text-[13px] tabular">
            Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}
          </p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
            <div
              className="h-full bg-accent transition-[width]"
              style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {outcome && !progress && (
        <p className="mb-4 rounded-xl border border-line bg-surface px-4 py-2.5 text-[13px]">
          {outcome}
        </p>
      )}

      {/*
        Refusals are named, never counted. "3 were refused" leaves somebody
        comparing a grid of 183 tiles against a folder to work out which three.
      */}
      {refused.length > 0 && (
        <ul className="mb-4 space-y-1 rounded-xl border border-warn bg-warn-bg px-4 py-3 text-[13px]">
          {refused.map((reason) => (
            <li key={reason} className="wrap-anywhere">
              {reason}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="mb-4 rounded-xl border border-crit px-4 py-2.5 text-[13px] text-crit">
          {error}
        </p>
      )}

      <div className="mb-4 flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            size={14}
            strokeWidth={2}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by file name"
            className="w-full rounded-xl border border-line bg-surface py-2.5 pr-3 pl-9 text-[13.5px]"
          />
        </div>

        <span className="shrink-0 text-[13px] text-ink-3 tabular">
          {matches.length} of {data?.length ?? 0}
        </span>
      </div>

      {isPending ? (
        <p className="px-5 py-10 text-center text-[14px] text-ink-3">Loading...</p>
      ) : matches.length === 0 ? (
        <p className="rounded-2xl border border-line bg-surface px-5 py-10 text-center text-[14px] text-ink-3">
          {data?.length ? 'Nothing matches that name.' : 'No photographs yet.'}
        </p>
      ) : (
        <ul className="grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {matches.map((asset) => (
            <Tile key={asset.id} asset={asset} />
          ))}
        </ul>
      )}
    </div>
  )
}

function Tile({ asset }: { asset: MediaAsset }) {
  return (
    <li className="overflow-hidden rounded-xl border border-line bg-surface">
      {/*
        Framed as she positioned it (D-199), so the library shows what the site
        shows rather than a differently-cropped version of the same photograph.
      */}
      <StudioImage
        src={asset.url}
        alt={asset.altText ?? ''}
        framing={asset.framing}
        className="block aspect-square w-full bg-surface-2"
      />
      <div className="px-2.5 py-2">
        <p className="truncate text-[12px] font-semibold" title={asset.fileName}>
          {displayName(asset.fileName)}
        </p>
        <p className="mt-0.5 text-[11.5px] text-ink-3 tabular">{fileSize(asset.sizeBytes)}</p>
      </div>
    </li>
  )
}
